import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  MessageSquare, Send, User, Bot, Clock, Phone, Mail, ExternalLink,
  UserPlus, X, Search, Eye, ChevronRight, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const ChatbotPage = () => {
  const [tab, setTab] = useState("sessions");
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureForm, setCaptureForm] = useState({ name: "", phone: "", email: "", project_interest: "", budget_min: "", budget_max: "" });

  // Live chatbot preview state
  const [previewSessionId, setPreviewSessionId] = useState(null);
  const [previewMessages, setPreviewMessages] = useState([]);
  const [previewInput, setPreviewInput] = useState("");
  const [previewSending, setPreviewSending] = useState(false);
  const chatEndRef = useRef(null);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/chatbot/sessions`);
      setSessions(res.data);
    } catch { toast.error("Failed to load sessions"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSessions(); }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [previewMessages]);

  const viewSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/chatbot/sessions/${sessionId}`);
      setSelectedSession(res.data);
    } catch { toast.error("Failed to load session"); }
  };

  const captureLead = async () => {
    if (!selectedSession) return;
    try {
      const payload = {
        ...captureForm,
        budget_min: captureForm.budget_min ? parseFloat(captureForm.budget_min) : null,
        budget_max: captureForm.budget_max ? parseFloat(captureForm.budget_max) : null,
      };
      const res = await axios.post(`${API}/chatbot/sessions/${selectedSession.session_id}/capture-lead`, payload);
      toast.success(`Lead captured: ${res.data.lead_id}`);
      setCaptureOpen(false);
      fetchSessions();
      viewSession(selectedSession.session_id);
    } catch { toast.error("Failed to capture lead"); }
  };

  const closeSession = async (sessionId) => {
    try {
      await axios.post(`${API}/chatbot/sessions/${sessionId}/close`);
      toast.success("Session closed");
      fetchSessions();
      if (selectedSession?.session_id === sessionId) setSelectedSession(null);
    } catch { toast.error("Failed to close session"); }
  };

  // Live preview functions
  const startPreview = async () => {
    try {
      const res = await axios.post(`${API}/chatbot/session`, { visitor_name: "Preview User" });
      setPreviewSessionId(res.data.session_id);
      setPreviewMessages([{ role: "assistant", content: res.data.welcome_message }]);
    } catch { toast.error("Failed to start chatbot preview"); }
  };

  const sendPreviewMessage = async () => {
    if (!previewInput.trim() || !previewSessionId) return;
    const msg = previewInput.trim();
    setPreviewInput("");
    setPreviewMessages(prev => [...prev, { role: "user", content: msg }]);
    setPreviewSending(true);
    try {
      const res = await axios.post(`${API}/chatbot/message`, {
        session_id: previewSessionId,
        message: msg,
      });
      setPreviewMessages(prev => [...prev, { role: "assistant", content: res.data.reply }]);
    } catch { toast.error("Message failed"); }
    finally { setPreviewSending(false); }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="chatbot-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Website Chatbot</h1>
          <p className="text-sm text-slate-500 mt-1">AI-powered buyer chatbot — review conversations and capture leads</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2 h-10" data-testid="chatbot-tabs">
            <TabsTrigger value="sessions">Conversations ({sessions.length})</TabsTrigger>
            <TabsTrigger value="preview">Live Preview</TabsTrigger>
          </TabsList>

          {/* ===== SESSIONS TAB ===== */}
          <TabsContent value="sessions" className="mt-4">
            <div className="grid lg:grid-cols-3 gap-4" style={{ minHeight: "500px" }}>
              {/* Session List */}
              <div className="space-y-2 lg:col-span-1">
                {loading ? <p className="text-center text-slate-400 py-8">Loading...</p> :
                sessions.length === 0 ? (
                  <Card><CardContent className="p-8 text-center text-slate-400">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p>No chatbot sessions yet</p>
                    <p className="text-xs mt-1">Switch to Live Preview to test the chatbot</p>
                  </CardContent></Card>
                ) : sessions.map(s => (
                  <Card key={s.session_id}
                    className={`cursor-pointer transition-all hover:shadow-sm ${selectedSession?.session_id === s.session_id ? "ring-2 ring-blue-400" : ""}`}
                    onClick={() => viewSession(s.session_id)}
                    data-testid={`session-card-${s.session_id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{s.visitor_name || "Anonymous"}</p>
                            <p className="text-xs text-slate-400">{new Date(s.started_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {s.lead_captured && <Badge className="bg-emerald-500 text-white text-xs">Lead</Badge>}
                          <Badge variant="outline" className={`text-xs ${s.status === "active" ? "text-blue-600" : s.status === "converted" ? "text-emerald-600" : "text-slate-400"}`}>
                            {s.status}
                          </Badge>
                        </div>
                      </div>
                      {(s.visitor_phone || s.visitor_email) && (
                        <div className="flex gap-3 mt-2 text-xs text-slate-500">
                          {s.visitor_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.visitor_phone}</span>}
                          {s.visitor_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{s.visitor_email}</span>}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Session Detail */}
              <div className="lg:col-span-2">
                {!selectedSession ? (
                  <Card><CardContent className="p-12 text-center text-slate-400">
                    <Eye className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p>Select a conversation to view</p>
                  </CardContent></Card>
                ) : (
                  <Card className="flex flex-col" style={{ height: "600px" }}>
                    <CardHeader className="pb-2 border-b flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-base">{selectedSession.visitor_name || "Anonymous"}</CardTitle>
                        <p className="text-xs text-slate-500">{selectedSession.session_id}</p>
                      </div>
                      <div className="flex gap-2">
                        {!selectedSession.lead_captured && (
                          <Button size="sm" variant="default" onClick={() => {
                            setCaptureForm({
                              name: selectedSession.visitor_name || "",
                              phone: selectedSession.visitor_phone || "",
                              email: selectedSession.visitor_email || "",
                              project_interest: selectedSession.project_interest || "",
                              budget_min: "", budget_max: "",
                            });
                            setCaptureOpen(true);
                          }} data-testid="capture-lead-btn">
                            <UserPlus className="w-4 h-4 mr-1" /> Capture Lead
                          </Button>
                        )}
                        {selectedSession.status === "active" && (
                          <Button size="sm" variant="outline" onClick={() => closeSession(selectedSession.session_id)}>
                            <X className="w-4 h-4 mr-1" /> Close
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="session-messages">
                      {(selectedSession.messages || []).map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                            msg.role === "user"
                              ? "bg-blue-500 text-white"
                              : "bg-slate-100 text-slate-800"
                          }`}>
                            {msg.content}
                            <p className={`text-[10px] mt-1 ${msg.role === "user" ? "text-blue-200" : "text-slate-400"}`}>
                              {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : ""}
                            </p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ===== LIVE PREVIEW TAB ===== */}
          <TabsContent value="preview" className="mt-4">
            <div className="flex justify-center">
              <Card className="w-full max-w-md" style={{ height: "600px" }}>
                <CardHeader className="pb-2 border-b bg-slate-900 text-white rounded-t-lg">
                  <div className="flex items-center gap-2">
                    <Bot className="w-5 h-5" />
                    <div>
                      <CardTitle className="text-sm text-white">PropertyCRM Assistant</CardTitle>
                      <p className="text-xs text-slate-300">Online</p>
                    </div>
                  </div>
                </CardHeader>

                {!previewSessionId ? (
                  <CardContent className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Bot className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-sm text-slate-500 mb-4">Test the buyer-facing chatbot</p>
                      <Button onClick={startPreview} data-testid="start-preview-btn">
                        <MessageSquare className="w-4 h-4 mr-2" /> Start Chat
                      </Button>
                    </div>
                  </CardContent>
                ) : (
                  <div className="flex flex-col" style={{ height: "calc(600px - 60px)" }}>
                    <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="preview-messages">
                      {previewMessages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                            msg.role === "user"
                              ? "bg-blue-500 text-white"
                              : "bg-slate-100 text-slate-800"
                          }`}>
                            {msg.content}
                          </div>
                        </div>
                      ))}
                      {previewSending && (
                        <div className="flex justify-start">
                          <div className="bg-slate-100 rounded-lg px-4 py-2 text-sm text-slate-400">
                            <span className="animate-pulse">Typing...</span>
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                    <div className="border-t p-3 flex gap-2">
                      <Input
                        value={previewInput}
                        onChange={(e) => setPreviewInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendPreviewMessage()}
                        placeholder="Type a message..."
                        disabled={previewSending}
                        data-testid="preview-input"
                      />
                      <Button onClick={sendPreviewMessage} disabled={previewSending || !previewInput.trim()} size="icon" data-testid="preview-send-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Capture Lead Dialog */}
        <Dialog open={captureOpen} onOpenChange={setCaptureOpen}>
          <DialogContent className="sm:max-w-md" data-testid="capture-lead-dialog">
            <DialogHeader><DialogTitle>Capture Lead from Chat</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Name</label>
                <Input value={captureForm.name} onChange={(e) => setCaptureForm({ ...captureForm, name: e.target.value })} data-testid="capture-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Phone</label>
                  <Input value={captureForm.phone} onChange={(e) => setCaptureForm({ ...captureForm, phone: e.target.value })} data-testid="capture-phone" /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
                  <Input value={captureForm.email} onChange={(e) => setCaptureForm({ ...captureForm, email: e.target.value })} /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Project Interest</label>
                <Input value={captureForm.project_interest} onChange={(e) => setCaptureForm({ ...captureForm, project_interest: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Budget Min</label>
                  <Input type="number" value={captureForm.budget_min} onChange={(e) => setCaptureForm({ ...captureForm, budget_min: e.target.value })} /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Budget Max</label>
                  <Input type="number" value={captureForm.budget_max} onChange={(e) => setCaptureForm({ ...captureForm, budget_max: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCaptureOpen(false)}>Cancel</Button>
              <Button onClick={captureLead} data-testid="capture-submit-btn">Capture Lead</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ChatbotPage;
